/**
 * Firestore CRUD 공통 훅
 *
 * table 파라미터는 슬래시 구분 전체 경로 (예: "Lesson/Branch/{branch_id}")
 * 대부분의 feature 훅이 이 레이어를 통해 DB에 접근함
 */
import {
  query,
  collection,
  getDocs,
  where,
  WhereFilterOp,
  limit,
  orderBy,
  OrderByDirection,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import useFirebaseConfig from "./useFirebaseConfig";

interface sql {
  field: string;
  operator: WhereFilterOp;
  data: string | null | number | string[] | boolean;
}

interface sort {
  name: string;
  direction: OrderByDirection;
}

const useFirestore = () => {
  const { dbFirestore } = useFirebaseConfig();

  const readFirestore = async (table: string) => {
    const firestore_query = query(collection(dbFirestore, table));
    return await getDocs(firestore_query)
      .then((querySnapshot) => {
        return querySnapshot;
      })
      .catch((err) => {
        return err;
      });
  };

  const readFirestoreOneDoc = async (table: string, id: string) => {
    const docRef = doc(dbFirestore, table, id);
    return await getDoc(docRef);
  };

  interface FirestoreDocument {
    id: string;
    progress?: number;
    videoUrl?: string;
    type?: string;
    submit_date?: Timestamp;
  }

  const readFirestoreCollection = async (
    table: string
  ): Promise<FirestoreDocument[]> => {
    try {
      const firestore_query = query(collection(dbFirestore, table));
      const querySnapshot = await getDocs(firestore_query);

      const data: FirestoreDocument[] = querySnapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() } as FirestoreDocument;
      });

      return data;
    } catch (error) {
      console.error("Error getting documents: ", error);
      return [];
    }
  };

  const readFirestoreWithQuery = async (table: string, sql: sql) => {
    const firestore_query = query(
      collection(dbFirestore, table),
      where(sql.field, sql.operator, sql.data)
    );
    return await getDocs(firestore_query)
      .then((querySnapshot) => {
        return querySnapshot;
      })
      .catch((err) => {
        return err;
      });
  };

  const readFirestoreWithSort = async (table: string, sort: sort) => {
    const firestore_query = query(
      collection(dbFirestore, table),
      orderBy(sort.name, sort.direction)
    );
    return await getDocs(firestore_query)
      .then((querySnapshot) => {
        return querySnapshot;
      })
      .catch((err) => {
        return err;
      });
  };

  const readFirestoreWithSortWithLimit = async (
    table: string,
    sort: sort,
    max_items: number
  ) => {
    const firestore_query = query(
      collection(dbFirestore, table),
      orderBy(sort.name, sort.direction),
      limit(max_items)
    );
    return await getDocs(firestore_query)
      .then((querySnapshot) => {
        return querySnapshot;
      })
      .catch((err) => {
        return err;
      });
  };

  /** effective_date.start <= today <= effective_date.end 복합 조건 쿼리 (과제 배포 기간 필터) */
  const readFirestoreWithRange = async (
    table: string,
    start: sql,
    end: sql,
  ) => {
    try {
      const firestore_query = query(
        collection(dbFirestore, table), 
        where(start.field, start.operator, start.data),
        where(end.field, end.operator, end.data)
      );
  
      return await getDocs(firestore_query)
      .then((querySnapshot) => {
        return querySnapshot;
      })
      .catch((err) => {
        return err;
      });
      
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const writeFirestore = async (table: string, id: string, data: any) => {
    return await setDoc(doc(dbFirestore, table, id), data)
      .then((res) => {
        return true;
      })
      .catch((err) => {
        console.error("writeFirestore error:", table, id, err);
        return false;
      });
  };

  const addFireStore = async (table: string, data: any) => {
    try {
      const docRef = await addDoc(collection(dbFirestore, table), data);
      return docRef.id;
    } catch (error) {
      throw error;
    }
  };

  /** 과제 콘텐츠 파일을 Storage에 업로드한 뒤 download URL을 Firestore 문서에 저장 */
  const addFirestoreWithFile = async (
    table: string,
    data: any,
    storage: any
  ) => {
    try {
      const fileUrls = await Promise.all(
        data.files.map(async (file: File) => {
          if (file) {
            const storageRef = ref(storage, `Report/${data.type}/${file.name}`);
            await uploadBytes(storageRef, file);

            return getDownloadURL(storageRef);
          } else {
            return null;
          }
        })
      );
      const validFileUrls = fileUrls.filter((url) => url !== null);
      const dataWithUrls = { ...data, files: validFileUrls };

      const docRef = await addDoc(collection(dbFirestore, table), dataWithUrls);

      return docRef.id;
    } catch (error) {
      console.log("error", error);
      return error;
    }
  };

  const deleteFirestore = async (table: string, docId: string) => {
    let result = false;
    try {
      result = await deleteDoc(doc(dbFirestore, table, docId)).then(res => {
        return true;
      });
    } catch (error) {
        return false;
    }
    return result;
  };
  const deleteFireStoreWithQuery = async (
    table: string,
    sql: sql
  ): Promise<number> => {
    const firestore_query = query(
      collection(dbFirestore, table),
      where(sql.field, sql.operator, sql.data)
    );
    const querySnapshot = await getDocs(firestore_query);
    const results = await Promise.all(
      querySnapshot.docs.map(async (docSnap) => {
        try {
          await deleteDoc(docSnap.ref);
          return true;
        } catch (error) {
          console.error("Error deleting document:", error);
          return false;
        }
      })
    );
    return results.filter(Boolean).length;
  };

  const updateFirestore = async (table: string, docId: string, data: any) => {
    try {
      await updateDoc(doc(dbFirestore, table, docId), data);
      return true;
    } catch (error) {
      throw error;
    }
  };

  const updateFirestoreWithQuery = async (
    table: string,
    sql: sql,
    data: any
  ) => {
    const firestore_query = query(
      collection(dbFirestore, table),
      where(sql.field, sql.operator, sql.data)
    );
    const querySnapshot = await getDocs(firestore_query);
    await Promise.all(
      querySnapshot.docs.map(async (docSnap) => {
        try {
          await updateDoc(docSnap.ref, data);
        } catch (error) {
          console.error("Error updating document:", error);
        }
      })
    );
  };

  const updateFirestoreWithFile = async (
    table: string,
    sql: sql,
    data: any,
    storage: any
  ) => {
    try {
      const firestore_query = query(
        collection(dbFirestore, table),
        where(sql.field, sql.operator, sql.data)
      );

      const querySnapshot = await getDocs(firestore_query);

      await Promise.all(
        querySnapshot.docs.map(async (docSnap) => {
          try {
            const fileUrls = await Promise.all(
              data.files.map(async (file: File | string) => {
                if (file instanceof File) {
                  const fileName = file.name || "";
                  const storageRef = ref(
                    storage,
                    `Report/${data.type}/${fileName}`
                  );
                  await uploadBytes(storageRef, file);

                  return getDownloadURL(storageRef);
                } else if (typeof file === "string") {
                  return file;
                } else {
                  return null;
                }
              })
            );

            const dataWithUrls = { ...data, files: fileUrls };
            await updateDoc(docSnap.ref, dataWithUrls);
          } catch (error) {
            console.error("Error updating document:", error);
          }
        })
      );
    } catch (error) {
      console.error("Error updating document:", error);
    }
  };

  async function changeDocumentId(collectionName: string, oldDocId: string, newDocId: string): Promise<void> {
    const oldDocRef = doc(dbFirestore, collectionName, oldDocId);
    const newDocRef = doc(dbFirestore, collectionName, newDocId);
  
    try {
      // 기존 문서 데이터 가져오기
      const docSnapshot = await getDoc(oldDocRef);
      if (!docSnapshot.exists()) {
        console.log(`No document found with ID: ${oldDocId}`);
        return;
      }
  
      const data = docSnapshot.data();
  
      // 새 문서에 데이터 쓰기
      await setDoc(newDocRef, data);
  
      // 기존 문서 삭제
      await deleteDoc(oldDocRef);
  
      console.log(`Document ID changed from ${oldDocId} to ${newDocId}`);
    } catch (error) {
      console.error("Error changing document ID: ", error);
    }
  }

  return {
    readFirestore,
    readFirestoreOneDoc,
    readFirestoreCollection,
    readFirestoreWithQuery,
    readFirestoreWithSort,
    readFirestoreWithSortWithLimit,
    readFirestoreWithRange,
    writeFirestore,
    addFireStore,
    addFirestoreWithFile,
    deleteFirestore,
    deleteFireStoreWithQuery,
    updateFirestore,
    updateFirestoreWithQuery,
    updateFirestoreWithFile,
    changeDocumentId
  };
};

export default useFirestore;
